import { createHttpClient } from '@adapters/HttpClient';
import { ShopsApiAdapter } from '@modules/Orders/List/adapters/ShopsApiAdapter';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { apiClientWH } from '@shared/services';
import { useCallback, useEffect, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface RefundsFiltersPanelProps {
  shopId: string;
  dateFrom: string;
  dateTo: string;
  onShopChange: (value: string) => void;
  onDateRangeChange: (from: string, to: string) => void;
  onClear: () => void;
}

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

export const RefundsFiltersPanel = ({
  shopId,
  dateFrom,
  dateTo,
  onShopChange,
  onDateRangeChange,
  onClear,
}: RefundsFiltersPanelProps) => {
  const { t } = useTranslation('refundsModule');
  const [shopOptions, setShopOptions] = useState<{ label: string; value: string }[]>([]);
  const [isLoadingShops, setIsLoadingShops] = useState(false);

  const activeFiltersCount = [shopId, dateFrom || dateTo].filter(Boolean).length;

  const loadShops = useCallback(async () => {
    try {
      setIsLoadingShops(true);
      const httpClient = createHttpClient(apiClientWH);
      const shopsAdapter = new ShopsApiAdapter(httpClient);
      const shops = await shopsAdapter.getAll();
      setShopOptions([
        { label: t('filters.all'), value: '' },
        ...shops.map((shop) => ({ label: shop.name, value: shop.id.toString() })),
      ]);
    } catch {
      setShopOptions([{ label: t('filters.all'), value: '' }]);
    } finally {
      setIsLoadingShops(false);
    }
  }, [t]);

  useEffect(() => {
    loadShops();
  }, [loadShops]);

  const handleDateRangeUpdate = ({ range }: { range: DateRange }) => {
    const from = range.from ? formatDateLocal(range.from) : '';
    const to = range.to ? formatDateLocal(range.to) : '';
    onDateRangeChange(from, to);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FilterField label={t('filters.shop_label')}>
          <Combobox
            options={shopOptions}
            value={shopId}
            setValue={onShopChange}
            placeholder={t('filters.all')}
            dataTestId="refunds-shop-selector"
            disabled={isLoadingShops}
          />
        </FilterField>
        <FilterField label={t('filters.date_range_label')}>
          <DateRangePicker
            initialDateFrom={dateFrom || undefined}
            initialDateTo={dateTo || undefined}
            onUpdate={handleDateRangeUpdate}
            align="start"
            showPresets={true}
            placeholder={t('filters.select_date_range')}
            dataTestId="refunds-date-range-picker"
            triggerClassName="h-[38px] text-sm"
          />
        </FilterField>
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={onClear}
            type="button"
          >
            Limpiar {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro' : 'filtros'}
          </Button>
        </div>
      )}
    </div>
  );
};
